import React, { useState, useRef, useCallback, useEffect } from 'react';
import Map, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const COLORES = {
  extremo: '#c53030',
  alto: '#dd6b20',
  moderado: '#d69e2e',
  bajo: '#8fa3a0'
};

const EXPLICACIONES = {
  extremo: 'Peligro máximo. Fuego de alta intensidad con condiciones criticas.',
  alto: 'Situación grave. Monitorear y preparar evacuación.',
  moderado: 'Actividad importante. Mantener vigilancia.',
  bajo: 'Actividad menor. Sin peligro inmediato.'
};

const FUENTES = {
  GOES_NRT: 'Satélite GOES · actualiza cada 15 min · cubre Sudamérica',
  FIRMS: 'Satélite NASA VIIRS · actualiza cada 3 horas · cobertura global',
  VIIRS: 'Satélite NASA VIIRS · actualiza cada 3 horas · cobertura global'
};

function frpExplicacion(frp) {
  const v = parseFloat(frp);
  if (v >= 50) return 'Intensidad MUY ALTA — incendio de gran escala';
  if (v >= 20) return 'Intensidad ALTA — fuego activo significativo';
  if (v >= 10) return 'Intensidad MEDIA — fuego activo moderado';
  return 'Intensidad BAJA — fuego pequeño o en inicio';
}

function distanciaKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function getBounds(predios) {
  if (!predios || predios.length === 0) return null;
  const lats = predios.map(p => parseFloat(p.latitud));
  const lons = predios.map(p => parseFloat(p.longitud));
  return {
    minLat: Math.min(...lats), maxLat: Math.max(...lats),
    minLon: Math.min(...lons), maxLon: Math.max(...lons),
    centerLat: (Math.min(...lats) + Math.max(...lats)) / 2,
    centerLon: (Math.min(...lons) + Math.max(...lons)) / 2,
  };
}

function generarCirculos(predios, radio) {
  const features = [];
  predios.forEach(predio => {
    const lat = parseFloat(predio.latitud);
    const lon = parseFloat(predio.longitud);
    const puntos = 64;
    const coords = [];
    for (let i = 0; i < puntos; i++) {
      const angulo = (i * 360) / puntos;
      const rad = (angulo * Math.PI) / 180;
      const dLat = (radio / 111) * Math.cos(rad);
      const dLon = (radio / (111 * Math.cos((lat * Math.PI) / 180))) * Math.sin(rad);
      coords.push([lon + dLon, lat + dLat]);
    }
    coords.push(coords[0]);
    features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } });
  });
  return { type: 'FeatureCollection', features };
}

function PopupFoco({ foco, onClose, predios }) {
  const lat = parseFloat(foco.latitud).toFixed(5);
  const lon = parseFloat(foco.longitud).toFixed(5);
  const mapsUrl = 'https://www.google.com/maps?q=' + lat + ',' + lon;
  const color = COLORES[foco.nivel_riesgo] || '#8fa3a0';
  const explicacion = EXPLICACIONES[foco.nivel_riesgo] || '';
  const fuenteTexto = FUENTES[foco.fuente] || ('Satélite ' + foco.fuente);
  const frpTexto = frpExplicacion(foco.frp);

  const distancias = (predios || []).map(p => ({
    nombre: p.nombre,
    km: distanciaKm(parseFloat(foco.latitud), parseFloat(foco.longitud), parseFloat(p.latitud), parseFloat(p.longitud))
  })).sort((a, b) => a.km - b.km);

  const linkProps = {
    href: mapsUrl,
    target: '_blank',
    rel: 'noopener noreferrer',
    style: {
      display: 'block',
      background: color,
      color: 'white',
      textAlign: 'center',
      padding: '8px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      textDecoration: 'none',
      marginTop: 10
    }
  };

  return (
    <div style={{ position: 'absolute', background: 'white', borderRadius: 12, padding: '14px 16px', boxShadow: '0 4px 24px rgba(0,0,0,0.25)', width: 260, zIndex: 10, top: 10, left: '50%', transform: 'translateX(-50%)', fontFamily: 'Inter, sans-serif', border: '2px solid ' + color, maxHeight: '80%', overflowY: 'auto' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ fontWeight: 700, color: color, fontSize: 14 }}>{foco.nivel_riesgo ? foco.nivel_riesgo.toUpperCase() : 'FOCO'}</div>
          <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{explicacion}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999', padding: 0, lineHeight: 1 }}>x</button>
      </div>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 4 }}>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>FRP: {parseFloat(foco.frp).toFixed(1)} MW</div>
          <div style={{ fontSize: 10, color: '#888' }}>{frpTexto}</div>
        </div>

        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>Fuente: {foco.fuente}</div>
          <div style={{ fontSize: 10, color: '#888' }}>{fuenteTexto}</div>
        </div>

        {foco.temperatura && (
          <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
            Temp: {foco.temperatura}C · Humedad: {foco.humedad || '—'}%
          </div>
        )}
        {foco.viento_kmh && (
          <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
            Viento: {foco.viento_kmh} km/h
          </div>
        )}

        <div style={{ fontSize: 10, color: '#aaa', marginBottom: 8 }}>{lat}, {lon}</div>

        {distancias.length > 0 && (
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 6 }}>Distancia a tus predios:</div>
            {distancias.map(d => (
              <div key={d.nombre} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#444', marginBottom: 4 }}>
                <span>📍 {d.nombre}</span>
                <span style={{ fontWeight: 600, color: d.km < 10 ? '#c53030' : d.km < 30 ? '#dd6b20' : '#6b9a3a' }}>
                  {d.km.toFixed(1)} km
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {React.createElement('a', linkProps, 'Ver ubicacion en Google Maps')}
    </div>
  );
}

const ESTILOS = [
  { id: 'outdoors-v12',        label: 'Mapa',     icon: '🗺️',  url: 'mapbox://styles/mapbox/outdoors-v12' },
  { id: 'satellite-streets-v12', label: 'Satélite', icon: '🛰️', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
];

export default function Mapa({ focos, predios = [], radio = 30, darkMode, predioSeleccionado }) {
  const [focoSeleccionado, setFocoSeleccionado] = useState(null);
  const [estiloIdx, setEstiloIdx] = useState(0);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const bounds = getBounds(predios);
  const centerLat = bounds ? bounds.centerLat : -35;
  const centerLon = bounds ? bounds.centerLon : -70;

  const [viewState, setViewState] = useState({
    longitude: centerLon,
    latitude: centerLat,
    zoom: predios.length > 0 ? 8 : 5
  });

  useEffect(() => {
    if (!predioSeleccionado || !mapRef.current || !mapLoaded) return;
    const lat = parseFloat(predioSeleccionado.latitud);
    const lon = parseFloat(predioSeleccionado.longitud);
    const radio_km = predioSeleccionado.radio_alerta_km || 15;
    // Zoom según radio: 15km→10, 10km→11, 5km→12
    const zoom = radio_km <= 5 ? 12 : radio_km <= 10 ? 11 : 10;
    mapRef.current.flyTo({ center: [lon, lat], zoom, duration: 1000 });
  }, [predioSeleccionado, mapLoaded]);

  const aplicarCirculos = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();
    const data = generarCirculos(predios, radio);
    const aplicar = () => {
      if (!map.isStyleLoaded()) { setTimeout(aplicar, 200); return; }
      try {
        if (map.getSource('radios')) {
          map.getSource('radios').setData(data);
        } else {
          map.addSource('radios', { type: 'geojson', data });
          map.addLayer({ id: 'radios-fill', type: 'fill', source: 'radios', paint: { 'fill-color': '#6b9a3a', 'fill-opacity': 0.1 } });
          map.addLayer({ id: 'radios-line', type: 'line', source: 'radios', paint: { 'line-color': '#6b9a3a', 'line-width': 2, 'line-dasharray': [4, 3], 'line-opacity': 0.9 } });
        }
      } catch (e) { console.error(e); }
    };
    aplicar();
  }, [predios, radio]);

  useEffect(() => {
    if (!mapLoaded) return;
    aplicarCirculos();
  }, [mapLoaded, aplicarCirculos]);

  const cambiarEstilo = (idx) => {
    setEstiloIdx(idx);
    // Re-aplicar círculos después de que el nuevo estilo cargue
    if (mapRef.current) {
      mapRef.current.getMap().once('style.load', aplicarCirculos);
    }
  };

  const centrarPredios = useCallback(() => {
    if (!bounds || !mapRef.current) return;
    if (predios.length === 1) {
      mapRef.current.flyTo({ center: [centerLon, centerLat], zoom: 10, duration: 1200 });
    } else {
      mapRef.current.fitBounds(
        [[bounds.minLon - 0.5, bounds.minLat - 0.5], [bounds.maxLon + 0.5, bounds.maxLat + 0.5]],
        { padding: 60, duration: 1200 }
      );
    }
  }, [bounds, predios]);

  const btnStyle = {
    width: 30, height: 30, background: 'white',
    border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer',
    fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,0.2)', color: '#333'
  };

  return (
    <div style={{ width: '100%', height: '100%', borderRadius: '12px', overflow: 'hidden', position: 'relative' }}>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onLoad={() => setTimeout(() => setMapLoaded(true), 800)}
        onClick={() => setFocoSeleccionado(null)}
        style={{ width: '100%', height: '100%' }}
        mapStyle={ESTILOS[estiloIdx].url}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
      >
        {predios.map((predio, i) => (
          <Marker key={'predio-' + i} longitude={parseFloat(predio.longitud)} latitude={parseFloat(predio.latitud)}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default' }}>
              <img src="/favicon.png" alt="predio" style={{ width: 44, height: 44, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' }} />
              <div style={{ background: '#3d6b2a', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, marginTop: 2, whiteSpace: 'nowrap', boxShadow: '0 1px 4px rgba(0,0,0,0.5)', fontFamily: 'Inter, sans-serif' }}>
                {predio.nombre}
              </div>
            </div>
          </Marker>
        ))}

        {focos.map((foco, i) => (
          <Marker
            key={'foco-' + i}
            longitude={parseFloat(foco.longitud)}
            latitude={parseFloat(foco.latitud)}
            onClick={e => { e.originalEvent.stopPropagation(); setFocoSeleccionado(foco); }}
          >
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              background: COLORES[foco.nivel_riesgo] || '#8fa3a0',
              border: '1.5px solid rgba(255,255,255,0.7)',
              cursor: 'pointer', boxShadow: '0 0 4px rgba(0,0,0,0.4)'
            }} />
          </Marker>
        ))}
      </Map>

      {focoSeleccionado && (
        <PopupFoco foco={focoSeleccionado} onClose={() => setFocoSeleccionado(null)} predios={predios} />
      )}

      {/* Controles zoom + centrar */}
      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button style={btnStyle} onClick={() => mapRef.current && mapRef.current.getMap().zoomIn()}>+</button>
        <button style={btnStyle} onClick={() => mapRef.current && mapRef.current.getMap().zoomOut()}>-</button>
        {predios.length > 0 && (
          <button style={btnStyle} title="Centrar en mi terreno" onClick={centrarPredios}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <circle cx="12" cy="12" r="4"/>
              <line x1="12" y1="2" x2="12" y2="6"/>
              <line x1="12" y1="18" x2="12" y2="22"/>
              <line x1="2" y1="12" x2="6" y2="12"/>
              <line x1="18" y1="12" x2="22" y2="12"/>
            </svg>
          </button>
        )}
      </div>

      {/* Selector de estilo */}
      <div style={{ position: 'absolute', bottom: 28, left: 10, display: 'flex', gap: 4, background: 'white', borderRadius: 8, padding: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
        {ESTILOS.map((e, i) => (
          <button
            key={e.id}
            onClick={() => cambiarEstilo(i)}
            style={{
              padding: '5px 10px', border: 'none', borderRadius: 6, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'Inter, sans-serif',
              background: estiloIdx === i ? '#3d6b2a' : 'transparent',
              color: estiloIdx === i ? 'white' : '#4a3f2a',
              transition: 'all .15s',
            }}
          >
            {e.icon} {e.label}
          </button>
        ))}
      </div>
    </div>
  );
}